'use client'

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'

export enum ThemeMode {
  LIGHT = 'light',
  DARK = 'dark',
  SYSTEM = 'system',
  OLED = 'oled'
}

export enum AccentColor {
  INDIGO = 'indigo',
  BLUE = 'blue',
  PURPLE = 'purple',
  PINK = 'pink',
  RED = 'red',
  ORANGE = 'orange',
  YELLOW = 'yellow',
  GREEN = 'green',
  TEAL = 'teal',
  CYAN = 'cyan',
  CUSTOM = 'custom'
}

export interface AppearanceSettings {
  theme: ThemeMode
  accentColor: AccentColor
  customAccentColor?: string // hex code
  // ... other settings are less relevant for the immediate color palette injection, but we'll include them for completeness if needed.
}

// We'll map the primary accent color to a lighter 'hover' and darker 'active' state if possible, or just use opacity variants via tailwind
const ACCENT_COLORS_MAP: Record<string, string> = {
  indigo: '#6366f1',
  blue: '#3b82f6',
  purple: '#a855f7',
  pink: '#ec4899',
  red: '#ef4444',
  orange: '#f97316',
  yellow: '#eab308',
  green: '#10b981', // emerald/green matching landing page
  teal: '#14b8a6',
  cyan: '#06b6d4',
}

interface AppearanceContextType {
  settings: AppearanceSettings
  setSettings: (settings: AppearanceSettings) => void
  refreshSettings: () => void
}

const defaultSettings: AppearanceSettings = {
  theme: ThemeMode.DARK,
  accentColor: AccentColor.GREEN, // Default to green to match landing page
}

const AppearanceContext = createContext<AppearanceContextType | undefined>(undefined)

export function useAppearance() {
  const context = useContext(AppearanceContext)
  if (!context) {
    return {
      settings: defaultSettings,
      setSettings: () => {},
      refreshSettings: () => {}
    }
  }
  return context
}

export function AppearanceProvider({ children }: { children: ReactNode }) {
  const [settings, setSettingsState] = useState<AppearanceSettings>(defaultSettings)
  const [mounted, setMounted] = useState(false)

  const loadSettings = () => {
    if (typeof window === 'undefined') return
    try {
      const saved = localStorage.getItem('linkbox-appearance-settings')
      if (saved) {
        const parsed = JSON.parse(saved)
        // Ensure default fallback if missing
        if (!parsed.accentColor) parsed.accentColor = AccentColor.GREEN;
        setSettingsState({ ...defaultSettings, ...parsed })
      }
    } catch (e) {
      console.error('Failed to parse appearance settings', e)
    }
  }

  useEffect(() => {
    loadSettings()
    setMounted(true)
  }, [])

  const setSettings = (newSettings: AppearanceSettings) => {
    setSettingsState(newSettings)
    if (typeof window !== 'undefined') {
      // Need to maintain existing settings outside of AppearanceSettings subset if they exist in localStorage
      const existingStr = localStorage.getItem('linkbox-appearance-settings')
      let existing = {}
      if (existingStr) {
          try { existing = JSON.parse(existingStr) } catch {}
      }
      localStorage.setItem('linkbox-appearance-settings', JSON.stringify({ ...existing, ...newSettings }))
    }
  }

  // Apply Theme and Colors
  useEffect(() => {
    if (!mounted || typeof window === 'undefined') return

    const root = document.documentElement
    
    // 1. Apply primary color
    let primaryHex = ACCENT_COLORS_MAP[settings.accentColor] || ACCENT_COLORS_MAP.green
    if (settings.accentColor === AccentColor.CUSTOM && settings.customAccentColor) {
      primaryHex = settings.customAccentColor
    }

    // Set CSS Variable used by Tailwind v4 generic theme
    root.style.setProperty('--color-primary', primaryHex)

    // Optional: Calculate a hover color programmatically if needed, but Tailwind color-mix(in oklab, var(--color-primary) 80%, black) is better
    // We'll rely on global.css to define hovering using color-mix or similar if needed

    // 2. Apply Theme Mode (light/dark/oled)
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const applyThemeClass = (mode: ThemeMode) => {
      root.classList.remove('light', 'dark', 'oled')
      
      let actualMode = mode
      if (mode === ThemeMode.SYSTEM) {
        actualMode = mediaQuery.matches ? ThemeMode.DARK : ThemeMode.LIGHT
      }

      if (actualMode === ThemeMode.DARK || actualMode === ThemeMode.OLED) {
        root.classList.add('dark')
        if (actualMode === ThemeMode.OLED) {
           root.style.setProperty('--background', '#000000') // Force pitch black
        } else {
           root.style.removeProperty('--background') // Let CSS handle standard dark mode
        }
      } else {
        root.classList.add('light')
        root.classList.remove('dark')
        root.style.removeProperty('--background')
      }
    }

    applyThemeClass(settings.theme)

    const sysListener = (e: MediaQueryListEvent) => {
      if (settings.theme === ThemeMode.SYSTEM) {
        applyThemeClass(ThemeMode.SYSTEM)
      }
    }

    mediaQuery.addEventListener('change', sysListener)
    return () => mediaQuery.removeEventListener('change', sysListener)

  }, [settings, mounted])

  return (
    <AppearanceContext.Provider value={{ settings, setSettings, refreshSettings: loadSettings }}>
      {children}
    </AppearanceContext.Provider>
  )
}
